import { useApiNotificationsV1PreferencesRetrieve, useFeatureFlags } from 'api';

import { useUpdateNotificationPreference } from '../useUpdateNotificationPreference/useUpdateNotificationPreference';

export type NotificationToggle = {
  labelKey:
    | 'navigation.userMenu.dailyQualityDigest'
    | 'navigation.userMenu.weeklyQualityDigest'
    | 'navigation.userMenu.belowSpecLoadEmailAlert'
    | 'navigation.userMenu.buyerEvidenceRequestCompletedEmailAlert'
    | 'navigation.userMenu.dailyProductionSummary';
  checked: boolean;
  onToggle: (checked: boolean) => void;
};

export const useNotificationPreferenceToggles = () => {
  const { data: notificationPreference, isLoading } = useApiNotificationsV1PreferencesRetrieve();
  const { getFlag } = useFeatureFlags();
  const isProductionManagementEnabled = getFlag('YIELD_MANAGEMENT_ENABLED', false);
  const updateNotificationPreference = useUpdateNotificationPreference();

  const toggleDailyQualityDigest = (checked: boolean) => {
    updateNotificationPreference({ data: { daily_quality_digest_enabled: checked } });
  };

  const toggleWeeklyQualityDigest = (checked: boolean) => {
    updateNotificationPreference({ data: { weekly_quality_digest_enabled: checked } });
  };

  const toggleBelowSpecLoadEmailAlert = (checked: boolean) => {
    updateNotificationPreference({ data: { notify_below_spec_load_enabled: checked } });
  };

  const toggleBuyerEvidenceRequestCompletedEmailAlert = (checked: boolean) => {
    updateNotificationPreference({ data: { notify_buyer_evidence_request_completed_enabled: checked } });
  };

  const toggleDailyProductionSummary = (checked: boolean) => {
    updateNotificationPreference({ data: { daily_production_summary_enabled: checked } });
  };

  const toggles: NotificationToggle[] = [
    {
      checked: notificationPreference?.daily_quality_digest_enabled ?? false,
      labelKey: 'navigation.userMenu.dailyQualityDigest',
      onToggle: toggleDailyQualityDigest,
    },
    {
      checked: notificationPreference?.weekly_quality_digest_enabled ?? false,
      labelKey: 'navigation.userMenu.weeklyQualityDigest',
      onToggle: toggleWeeklyQualityDigest,
    },
    {
      checked: notificationPreference?.notify_below_spec_load_enabled ?? false,
      labelKey: 'navigation.userMenu.belowSpecLoadEmailAlert',
      onToggle: toggleBelowSpecLoadEmailAlert,
    },
    {
      checked: notificationPreference?.notify_buyer_evidence_request_completed_enabled ?? false,
      labelKey: 'navigation.userMenu.buyerEvidenceRequestCompletedEmailAlert',
      onToggle: toggleBuyerEvidenceRequestCompletedEmailAlert,
    },
    ...(isProductionManagementEnabled
      ? [
          {
            checked: notificationPreference?.daily_production_summary_enabled ?? false,
            labelKey: 'navigation.userMenu.dailyProductionSummary' as const,
            onToggle: toggleDailyProductionSummary,
          },
        ]
      : []),
  ];

  const emailEnabled = notificationPreference?.email_enabled ?? true;

  const toggleEmailEnabled = (checked: boolean) => {
    updateNotificationPreference({ data: { email_enabled: checked } });
  };

  return { emailEnabled, isLoading, toggleEmailEnabled, toggles };
};
